/**
 * RTWO Native Currency Engine — Repositories
 *
 * In-memory stores remain empty until adapters ingest real currency data.
 */

import type {
  AllocationId,
  CurrencyId,
  EventId,
  FeePolicyId,
  PolicyId,
  ScheduleId,
  SupplyStateId,
  RTWOAllocationPolicy,
  RTWOEmissionSchedule,
  RTWOFeePolicy,
  RTWOMonetaryPolicy,
  RTWONativeCurrency,
  RTWONativeCurrencyEvent,
  RTWOSupplyState,
} from '../domain-models';

class InMemoryMapRepository<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly getKey: (entity: T) => string) {}

  findAll(): T[] {
    return Array.from(this.store.values());
  }

  findById(id: string): T | null {
    return this.store.get(id) ?? null;
  }

  save(entity: T): void {
    this.store.set(this.getKey(entity), entity);
  }
}

export class CurrencyRepository {
  private readonly byId = new InMemoryMapRepository<RTWONativeCurrency>(
    (currency) => currency.currencyId
  );

  findAll(): RTWONativeCurrency[] {
    return this.byId.findAll();
  }

  findById(currencyId: CurrencyId): RTWONativeCurrency | null {
    return this.byId.findById(currencyId);
  }

  save(currency: RTWONativeCurrency): void {
    this.byId.save(currency);
  }
}

export class MonetaryPolicyRepository {
  private readonly byId = new InMemoryMapRepository<RTWOMonetaryPolicy>(
    (policy) => policy.policyId
  );
  private readonly byCurrencyId = new Map<CurrencyId, PolicyId>();

  findAll(): RTWOMonetaryPolicy[] {
    return this.byId.findAll();
  }

  findById(policyId: PolicyId): RTWOMonetaryPolicy | null {
    return this.byId.findById(policyId);
  }

  findByCurrencyId(currencyId: CurrencyId): RTWOMonetaryPolicy | null {
    const policyId = this.byCurrencyId.get(currencyId);
    if (!policyId) return null;
    return this.byId.findById(policyId);
  }

  save(policy: RTWOMonetaryPolicy): void {
    this.byId.save(policy);
    this.byCurrencyId.set(policy.currencyId, policy.policyId);
  }
}

export class SupplyStateRepository {
  private readonly byId = new InMemoryMapRepository<RTWOSupplyState>(
    (state) => state.supplyStateId
  );
  private readonly byCurrencyId = new Map<CurrencyId, SupplyStateId>();

  findAll(): RTWOSupplyState[] {
    return this.byId.findAll();
  }

  findById(supplyStateId: SupplyStateId): RTWOSupplyState | null {
    return this.byId.findById(supplyStateId);
  }

  findByCurrencyId(currencyId: CurrencyId): RTWOSupplyState | null {
    const supplyStateId = this.byCurrencyId.get(currencyId);
    if (!supplyStateId) return null;
    return this.byId.findById(supplyStateId);
  }

  save(state: RTWOSupplyState): void {
    this.byId.save(state);
    this.byCurrencyId.set(state.currencyId, state.supplyStateId);
  }
}

export class AllocationPolicyRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAllocationPolicy>(
    (policy) => policy.allocationId
  );

  findAll(): RTWOAllocationPolicy[] {
    return this.byId.findAll();
  }

  findById(allocationId: AllocationId): RTWOAllocationPolicy | null {
    return this.byId.findById(allocationId);
  }

  findByCurrencyId(currencyId: CurrencyId): RTWOAllocationPolicy[] {
    return this.findAll().filter((policy) => policy.currencyId === currencyId);
  }

  save(policy: RTWOAllocationPolicy): void {
    this.byId.save(policy);
  }
}

export class FeePolicyRepository {
  private readonly byId = new InMemoryMapRepository<RTWOFeePolicy>((policy) => policy.feePolicyId);
  private readonly byCurrencyId = new Map<CurrencyId, FeePolicyId>();

  findAll(): RTWOFeePolicy[] {
    return this.byId.findAll();
  }

  findById(feePolicyId: FeePolicyId): RTWOFeePolicy | null {
    return this.byId.findById(feePolicyId);
  }

  findByCurrencyId(currencyId: CurrencyId): RTWOFeePolicy | null {
    const feePolicyId = this.byCurrencyId.get(currencyId);
    if (!feePolicyId) return null;
    return this.byId.findById(feePolicyId);
  }

  save(policy: RTWOFeePolicy): void {
    this.byId.save(policy);
    this.byCurrencyId.set(policy.currencyId, policy.feePolicyId);
  }
}

export class EmissionScheduleRepository {
  private readonly byId = new InMemoryMapRepository<RTWOEmissionSchedule>(
    (schedule) => schedule.scheduleId
  );

  findAll(): RTWOEmissionSchedule[] {
    return this.byId.findAll();
  }

  findById(scheduleId: ScheduleId): RTWOEmissionSchedule | null {
    return this.byId.findById(scheduleId);
  }

  findByCurrencyId(currencyId: CurrencyId): RTWOEmissionSchedule[] {
    return this.findAll().filter((schedule) => schedule.currencyId === currencyId);
  }

  save(schedule: RTWOEmissionSchedule): void {
    this.byId.save(schedule);
  }
}

export class NativeCurrencyEventRepository {
  private readonly byId = new InMemoryMapRepository<RTWONativeCurrencyEvent>(
    (event) => event.eventId
  );

  findAll(): RTWONativeCurrencyEvent[] {
    return this.byId.findAll();
  }

  findById(eventId: EventId): RTWONativeCurrencyEvent | null {
    return this.byId.findById(eventId);
  }

  findByCurrencyId(currencyId: CurrencyId): RTWONativeCurrencyEvent[] {
    return this.findAll().filter((event) => event.currencyId === currencyId);
  }

  save(event: RTWONativeCurrencyEvent): void {
    this.byId.save(event);
  }
}

export interface NATVRepositories {
  currencies: CurrencyRepository;
  monetaryPolicies: MonetaryPolicyRepository;
  supplyStates: SupplyStateRepository;
  allocationPolicies: AllocationPolicyRepository;
  feePolicies: FeePolicyRepository;
  emissionSchedules: EmissionScheduleRepository;
  events: NativeCurrencyEventRepository;
}

export function createNATVRepositories(): NATVRepositories {
  return {
    currencies: new CurrencyRepository(),
    monetaryPolicies: new MonetaryPolicyRepository(),
    supplyStates: new SupplyStateRepository(),
    allocationPolicies: new AllocationPolicyRepository(),
    feePolicies: new FeePolicyRepository(),
    emissionSchedules: new EmissionScheduleRepository(),
    events: new NativeCurrencyEventRepository(),
  };
}
